import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import DocumentUpload from './DocumentUpload';
import ShareConventionLink from './ShareConventionLink';

function Tooltip({ text }) {
  return (
    <span className="relative inline-block group align-middle ml-1">
      <span className="cursor-help text-blue-500 text-xs border border-blue-400 rounded-full w-4 h-4 inline-flex items-center justify-center font-bold">?</span>
      <span className="absolute z-20 left-1/2 -translate-x-1/2 bottom-full mb-2 w-96 max-w-xs bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity leading-relaxed shadow-lg">
        {text}
        <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
      </span>
    </span>
  );
}

// Convertit l'ancien format JSON structuré en texte lisible (rétrocompat)
function legacyScheduleToText(schedule) {
  if (!schedule || typeof schedule !== 'object') return '';
  const JOURS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
  return JOURS
    .filter(j => schedule[j] && (schedule[j].matin || schedule[j].apmidi))
    .map(j => {
      const s = schedule[j];
      const parts = [];
      if (s.matin) parts.push(`matin: ${s.matin}`);
      if (s.apmidi) parts.push(`ap-midi: ${s.apmidi}`);
      return `${j.charAt(0).toUpperCase() + j.slice(1)}: ${parts.join(', ')}`;
    })
    .join('\n');
}

const emptyForm = () => ({
  // Entreprise
  siret: '', companyId: '', companyName: '', companyAddress: '', companyCity: '',
  companyZip: '', companyApe: '', companyDomain: '', companyPhone: '', companyEmail: '',
  assureur: '', contratAssurance: '',
  // Représentant légal entreprise
  repCivilite: '', repName: '', repFirstName: '', repFunction: '',
  // Tuteur
  tutorCivilite: '', tutorName: '', tutorFirstName: '', tutorFunction: '', tutorEmail: '', tutorPhone: '',
  repPhone: '',
  // Stage
  classeId: '', startDate: '', endDate: '', totalDays: '', weeklyHours: '35',
  schedule: '', missions: '', skills: '', gratification: '', gratificationPeriode: '',
  // Avantages & cas particuliers
  avantagesPrive: '', avantagesPublic: '', casParticuliers: '',
  // Représentant légal étudiant
  isMineur: false, repLegalNom: '', repLegalPrenom: '', repLegalEmail: '',
  repLegalTelephone: '', repLegalLien: '',
});

export default function ConventionForm({ conventionId, initialPeriodeId, initialClasseId, onSaved, onCancel }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm());
  const [classes, setClasses] = useState([]);
  const [periode, setPeriode] = useState(null); // période associée si initialPeriodeId
  const [siretLoading, setSiretLoading] = useState(false);
  const [siretError, setSiretError] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedId, setSavedId] = useState(conventionId || null);
  const [showAnnexe, setShowAnnexe] = useState(false);
  const [dateNaissance, setDateNaissance] = useState(null); // pour calcul mineur auto
  const [studentName, setStudentName] = useState('');

  // Calcul automatique mineur : étudiant < 18 ans à la date de début de stage
  useEffect(() => {
    if (!dateNaissance || !form.startDate) return;
    const dob = new Date(dateNaissance);
    const start = new Date(form.startDate);
    const age18 = new Date(dob);
    age18.setFullYear(age18.getFullYear() + 18);
    setForm(prev => ({ ...prev, isMineur: start < age18 }));
  }, [dateNaissance, form.startDate]);

  // Calcul automatique du nombre de jours ouvrés dès que les dates changent
  useEffect(() => {
    if (!form.startDate || !form.endDate) return;
    const start = new Date(form.startDate);
    const end = new Date(form.endDate);
    if (end < start) return;

    // Jours fériés français (calculés dynamiquement pour l'année en cours et suivante)
    const feriesFixes = (y) => [
      `${y}-01-01`, `${y}-05-01`, `${y}-05-08`,
      `${y}-07-14`, `${y}-08-15`, `${y}-11-01`,
      `${y}-11-11`, `${y}-12-25`,
    ];
    const easterDate = (y) => {
      const a = y % 19, b = Math.floor(y / 100), c = y % 100;
      const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
      const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
      const i = Math.floor(c / 4), k = c % 4;
      const l = (32 + 2 * e + 2 * i - h - k) % 7;
      const m = Math.floor((a + 11 * h + 22 * l) / 451);
      const month = Math.floor((h + l - 7 * m + 114) / 31);
      const day = ((h + l - 7 * m + 114) % 31) + 1;
      return new Date(y, month - 1, day);
    };
    const localISO = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

    const feries = (y) => {
      const easter = easterDate(y);
      const lundi = new Date(easter); lundi.setDate(easter.getDate() + 1);
      const ascension = new Date(easter); ascension.setDate(easter.getDate() + 39);
      const pentecote = new Date(easter); pentecote.setDate(easter.getDate() + 50);
      return new Set([
        ...feriesFixes(y),
        localISO(lundi),
        localISO(ascension),
        localISO(pentecote),
      ]);
    };

    const years = new Set([start.getFullYear(), end.getFullYear()]);
    const joursFeeries = new Set([...[...years].flatMap(y => [...feries(y)])]);

    let count = 0;
    const cur = new Date(start);
    while (cur <= end) {
      const dow = cur.getDay();
      const iso = localISO(cur);
      if (dow !== 0 && dow !== 6 && !joursFeeries.has(iso)) count++;
      cur.setDate(cur.getDate() + 1);
    }
    setForm(prev => ({ ...prev, totalDays: String(count) }));
  }, [form.startDate, form.endDate]);

  // Charger la date de naissance de l'étudiant pour le calcul mineur
  useEffect(() => {
    api.get('/auth/me').then(res => {
      if (res.data.dateNaissance) setDateNaissance(res.data.dateNaissance);
      if (res.data.firstName || res.data.lastName)
        setStudentName(`${res.data.firstName || ''} ${res.data.lastName || ''}`.trim());
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/classes').then(res => {
      setClasses(res.data);
      if (!conventionId) {
        if (initialClasseId) {
          setForm(prev => ({ ...prev, classeId: initialClasseId }));
        } else if (res.data.length === 1) {
          setForm(prev => ({ ...prev, classeId: res.data[0].id }));
        }
      }
    }).catch(() => {});

    // Charger la période si fournie
    if (initialPeriodeId && !conventionId) {
      // Trouver la période dans les classes déjà chargées ou via l'API
      api.get('/classes').then(res => {
        for (const cls of res.data) {
          const p = cls.periodes?.find(p => p.id === initialPeriodeId);
          if (p) {
            setPeriode(p);
            setForm(prev => ({
              ...prev,
              classeId: initialClasseId || cls.id,
              startDate: p.startDate.split('T')[0],
              endDate: p.endDate.split('T')[0],
            }));
            break;
          }
        }
      }).catch(() => {});
    }

    if (conventionId) {
      api.get(`/conventions/${conventionId}`).then(res => {
        const c = res.data;
        const scheduleRaw = c.schedule;
        const schedule = typeof scheduleRaw === 'string'
          ? scheduleRaw
          : legacyScheduleToText(scheduleRaw);
        setForm({
          siret: c.company?.siret || '',
          companyId: c.companyId || '',
          companyName: c.company?.name || '',
          companyAddress: c.company?.address || '',
          companyCity: c.company?.city || '',
          companyZip: c.company?.zipCode || '',
          companyApe: c.company?.apeCode || '',
          companyDomain: c.company?.activityDomain || '',
          companyPhone: c.company?.phone || '',
          companyEmail: c.company?.email || '',
          assureur: c.assureur || '',
          contratAssurance: c.contratAssurance || '',
          repCivilite: c.repCivilite || '',
          repName: c.repName || '',
          repFirstName: c.repFirstName || '',
          repFunction: c.repFunction || '',
          tutorCivilite: c.tutorCivilite || '',
          tutorName: c.tutorName || '',
          tutorFirstName: c.tutorFirstName || '',
          tutorFunction: c.tutorFunction || '',
          tutorEmail: c.tutorEmail || '',
          tutorPhone: c.tutorPhone || '',
          repPhone: c.repPhone || '',
          classeId: c.classeId || '',
          startDate: c.startDate ? c.startDate.split('T')[0] : '',
          endDate: c.endDate ? c.endDate.split('T')[0] : '',
          totalDays: c.totalDays != null ? String(c.totalDays) : '',
          weeklyHours: c.weeklyHours != null ? String(c.weeklyHours) : '',
          schedule,
          missions: c.missions || '',
          skills: c.skills || '',
          gratification: c.gratification != null ? String(c.gratification) : '',
          gratificationPeriode: c.gratificationPeriode || '',
          avantagesPrive: c.avantagesPrive || '',
          avantagesPublic: c.avantagesPublic || '',
          casParticuliers: c.casParticuliers || '',
          isMineur: c.isMineur || false,
          repLegalNom: c.repLegalNom || '',
          repLegalPrenom: c.repLegalPrenom || '',
          repLegalEmail: c.repLegalEmail || '',
          repLegalTelephone: c.repLegalTelephone || '',
          repLegalLien: c.repLegalLien || '',
        });
      }).catch(console.error);
    }
  }, [conventionId]);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const lookupSiret = async () => {
    if (!/^\d{14}$/.test(form.siret)) {
      setSiretError('Le SIRET doit contenir exactement 14 chiffres');
      return;
    }
    setSiretLoading(true);
    setSiretError('');
    try {
      const res = await api.get(`/companies/siret/${form.siret}`);
      const c = res.data;
      setForm(prev => ({
        ...prev,
        companyId: c.id,
        companyName: c.name,
        companyAddress: c.address,
        companyCity: c.city,
        companyZip: c.zipCode,
        companyApe: c.apeCode,
        companyDomain: c.activityDomain,
      }));
    } catch (err) {
      setSiretError(err.response?.data?.error || 'Erreur lors de la recherche');
    } finally {
      setSiretLoading(false);
    }
  };

  const handleSave = async (submit = false) => {
    if (submit && !form.companyId) return setError('Veuillez d\'abord rechercher et valider le SIRET avant de soumettre');
    setSaving(true);
    setError('');
    try {
      const payload = {
        companyId: form.companyId,
        companyDomain: form.companyDomain,
        companyEmail: form.companyEmail,
        companyPhone: form.companyPhone,
        classeId: form.classeId,
        ...(initialPeriodeId !== undefined && { stagePeriodeId: initialPeriodeId }),
        startDate: form.startDate,
        endDate: form.endDate,
        totalDays: parseInt(form.totalDays) || 0,
        weeklyHours: parseInt(form.weeklyHours) || 0,
        schedule: form.schedule || null,
        missions: form.missions || null,
        skills: form.skills || null,
        gratification: form.gratification || null,
        gratificationPeriode: form.gratificationPeriode || null,
        assureur: form.assureur,
        contratAssurance: form.contratAssurance,
        tutorName: form.tutorName,
        tutorFirstName: form.tutorFirstName,
        tutorFunction: form.tutorFunction,
        tutorEmail: form.tutorEmail,
        tutorPhone: form.tutorPhone,
        repCivilite: form.repCivilite || null,
        repName: form.repName,
        repFirstName: form.repFirstName,
        repFunction: form.repFunction,
        repPhone: form.repPhone,
        tutorCivilite: form.tutorCivilite || null,
        avantagesPrive: form.avantagesPrive || null,
        avantagesPublic: form.avantagesPublic || null,
        casParticuliers: form.casParticuliers || null,
        isMineur: form.isMineur,
        repLegalNom: form.repLegalNom,
        repLegalPrenom: form.repLegalPrenom,
        repLegalEmail: form.repLegalEmail,
        repLegalTelephone: form.repLegalTelephone,
        repLegalLien: form.repLegalLien,
      };

      let res;
      if (savedId) {
        res = await api.put(`/conventions/${savedId}`, payload);
      } else {
        res = await api.post('/conventions', payload);
        setSavedId(res.data.id);
      }

      if (submit) {
        await api.post(`/conventions/${res.data.id}/submit`);
      }

      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">
            {conventionId ? 'Modifier la convention' : 'Nouvelle convention de stage'}
          </h2>
          {savedId && <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">Brouillon enregistré</span>}
        </div>
        <button onClick={onCancel} className="text-sm text-gray-500 hover:text-gray-700">← Retour</button>
      </div>

      {/* Étapes */}
      <div className="flex gap-1 mb-8">
        {[1, 2, 3, 4].map(s => (
          <div
            key={s}
            className={`flex-1 h-1.5 rounded-full transition-colors ${step >= s ? 'bg-blue-600' : 'bg-gray-200'}`}
          />
        ))}
      </div>

      {studentName && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center gap-2 text-sm text-blue-800 mb-4">
          <span className="font-medium">Convention de stage —</span>
          <span>{studentName}</span>
        </div>
      )}

      {savedId && (
        <div className="mb-4">
          <ShareConventionLink conventionId={savedId} />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
          {error}
        </div>
      )}

      {/* Étape 1 : Entreprise */}
      {step === 1 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
          <h3 className="font-medium text-gray-800">Étape 1 — Entreprise d'accueil</h3>

          <div>
            <label className={labelClass}>SIRET de l'entreprise</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={form.siret}
                onChange={e => set('siret', e.target.value.replace(/\s/g, ''))}
                placeholder="14 chiffres"
                maxLength={14}
                className={inputClass}
              />
              <button
                onClick={lookupSiret}
                disabled={siretLoading}
                className="shrink-0 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                {siretLoading ? '...' : 'Rechercher'}
              </button>
            </div>
            {siretError && <p className="text-red-600 text-xs mt-1">{siretError}</p>}
          </div>

          {form.companyName && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
              <p className="text-sm font-medium text-blue-800">{form.companyName}</p>
              <p className="text-xs text-blue-600">{form.companyAddress}, {form.companyZip} {form.companyCity}</p>
              <p className="text-xs text-blue-600">APE : {form.companyApe}</p>

              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="col-span-2">
                  <label className={labelClass}>Domaine d'activité</label>
                  <input type="text" value={form.companyDomain} onChange={e => set('companyDomain', e.target.value)} className={inputClass} placeholder="ex: Commerce de détail, Informatique…" />
                </div>
                <div className="col-span-2">
                  <label className={labelClass}>Téléphone entreprise</label>
                  <input type="tel" value={form.companyPhone} onChange={e => set('companyPhone', e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Assureur</label>
                  <input type="text" value={form.assureur} onChange={e => set('assureur', e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>N° contrat assurance</label>
                  <input type="text" value={form.contratAssurance} onChange={e => set('contratAssurance', e.target.value)} className={inputClass} />
                </div>
              </div>
            </div>
          )}

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Représentant légal de l'entreprise</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Civilité</label>
                <select value={form.repCivilite} onChange={e => set('repCivilite', e.target.value)} className={inputClass}>
                  <option value="">—</option>
                  <option value="M.">M.</option>
                  <option value="Mme">Mme</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Fonction</label>
                <input type="text" value={form.repFunction} onChange={e => set('repFunction', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Nom</label>
                <input type="text" value={form.repName} onChange={e => set('repName', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Prénom</label>
                <input type="text" value={form.repFirstName} onChange={e => set('repFirstName', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Téléphone</label>
                <input type="tel" value={form.repPhone} onChange={e => set('repPhone', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Email entreprise</label>
                <input type="email" value={form.companyEmail} onChange={e => set('companyEmail', e.target.value)} className={inputClass} />
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Tuteur en entreprise</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Civilité</label>
                <select value={form.tutorCivilite} onChange={e => set('tutorCivilite', e.target.value)} className={inputClass}>
                  <option value="">—</option>
                  <option value="M.">M.</option>
                  <option value="Mme">Mme</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Fonction</label>
                <input type="text" value={form.tutorFunction} onChange={e => set('tutorFunction', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Nom</label>
                <input type="text" value={form.tutorName} onChange={e => set('tutorName', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Prénom</label>
                <input type="text" value={form.tutorFirstName} onChange={e => set('tutorFirstName', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Email</label>
                <input type="email" value={form.tutorEmail} onChange={e => set('tutorEmail', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Téléphone</label>
                <input type="tel" value={form.tutorPhone} onChange={e => set('tutorPhone', e.target.value)} className={inputClass} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Étape 2 : Période */}
      {step === 2 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
          <h3 className="font-medium text-gray-800">Étape 2 — Période et conditions</h3>

          {periode && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center gap-3">
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800">{periode.nom}</p>
                <p className="text-xs text-blue-600">
                  {new Date(periode.startDate).toLocaleDateString('fr-FR')} → {new Date(periode.endDate).toLocaleDateString('fr-FR')}
                </p>
              </div>
              <span className="text-xs text-blue-500 bg-blue-100 px-2 py-0.5 rounded-full">Dates pré-remplies</span>
            </div>
          )}

          <div>
            <label className={labelClass}>Classe</label>
            {form.classeId ? (() => {
              const cls = classes.find(c => c.id === form.classeId);
              const notConfigured = cls && (!cls.missionsDefaut || !cls.competencesDefaut || !cls.modalitesEncadrement || !cls.modalitesEvaluation || !cls.travailAFournir);
              return (
                <>
                  <div className={`${inputClass} bg-gray-50 text-gray-700 cursor-default select-none`}>
                    {cls ? `${cls.name} — ${cls.schoolYear?.label}` : form.classeId}
                  </div>
                  {notConfigured && (
                    <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
                      Votre enseignant n'a pas encore configuré cette classe (missions, modalités…). Vous pouvez enregistrer un brouillon, mais la soumission sera bloquée jusqu'à la configuration.
                    </div>
                  )}
                </>
              );
            })() : (
              <select value={form.classeId} onChange={e => set('classeId', e.target.value)} className={inputClass}>
                <option value="">Sélectionner une classe</option>
                {classes.map(c => (
                  <option key={c.id} value={c.id}>{c.name} — {c.schoolYear?.label}</option>
                ))}
              </select>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Date de début</label>
              <input
                type="date"
                value={form.startDate}
                onChange={e => set('startDate', e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Date de fin</label>
              <input
                type="date"
                value={form.endDate}
                onChange={e => set('endDate', e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Nombre de jours</label>
              <input
                type="number"
                value={form.totalDays}
                readOnly
                className={`${inputClass} bg-gray-50 text-gray-500 cursor-default`}
                tabIndex={-1}
              />
              <p className="text-xs text-gray-400 mt-1">Calculé automatiquement</p>
            </div>
            <div>
              <label className={labelClass}>Heures hebdomadaires</label>
              <input type="number" value={form.weeklyHours} onChange={e => set('weeklyHours', e.target.value)} className={inputClass} min="1" max="48" />
            </div>
          </div>

          <div>
            <label className={labelClass}>
              Gratification (optionnel)
              <Tooltip text={"Article 5 – Gratification\n\nLorsque la durée du stage est supérieure à deux mois consécutifs ou non, celui-ci fait obligatoirement l'objet d'une gratification.\n\nLe montant horaire est fixé à 15 % du plafond horaire de la sécurité sociale. Une convention de branche peut définir un montant supérieur.\n\nLa gratification est due sans préjudice du remboursement des frais engagés par le stagiaire et des avantages offerts pour la restauration, l'hébergement et le transport.\n\nEn cas de suspension ou de résiliation, le montant est proratisé en fonction de la durée du stage effectué."} />
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type="number"
                  value={form.gratification}
                  onChange={e => set('gratification', e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className={inputClass}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">€</span>
              </div>
              <select
                value={form.gratificationPeriode}
                onChange={e => set('gratificationPeriode', e.target.value)}
                className={`w-36 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${!form.gratification ? 'opacity-50' : ''}`}
                disabled={!form.gratification}
              >
                <option value="">— par —</option>
                <option value="heure">par heure</option>
                <option value="jour">par jour</option>
                <option value="mois">par mois</option>
              </select>
            </div>
          </div>

          <div>
            <label className={labelClass}>Horaires de travail</label>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2">
              La durée de présence hebdomadaire ne peut excéder <strong>30 heures</strong> pour les étudiants de moins de 15 ans et <strong>35 heures</strong> pour les étudiants de plus de 15 ans.
            </p>
            <textarea
              value={form.schedule}
              onChange={e => set('schedule', e.target.value)}
              rows={4}
              placeholder={"Ex :\nLundi - Vendredi : 9h00 - 12h30 / 13h30 - 17h00\nMercredi : 9h00 - 12h00"}
              className={`${inputClass} resize-none`}
            />
          </div>
        </div>
      )}

      {/* Étape 3 : Informations complémentaires */}
      {step === 3 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
          <h3 className="font-medium text-gray-800">Étape 3 — Informations complémentaires</h3>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
            Les missions, compétences et modalités sont définies par votre enseignant pour votre classe et seront automatiquement incluses dans la convention.
          </div>

          <div>
            <label className={labelClass}>Cas particuliers (Art. 3 — travail de nuit, dimanche, jour férié)</label>
            <textarea
              value={form.casParticuliers}
              onChange={e => set('casParticuliers', e.target.value)}
              rows={3}
              placeholder="Ex : travail le samedi matin autorisé, pas de travail de nuit…"
              className={`${inputClass} resize-none`}
            />
          </div>
          <div>
            <label className={labelClass}>
              Avantages accordés — droit privé (Art. 5bis)
              <Tooltip text={"Article 5bis – Accès aux droits des salariés (Organisme de droit privé)\n\nLe stagiaire bénéficie des protections et droits mentionnés aux articles L.1121-1, L.1152-1 et L.1153-1 du code du travail, dans les mêmes conditions que les salariés.\n\nIl a accès au restaurant d'entreprise ou aux titres-restaurants (art. L.3262-1) et bénéficie de la prise en charge des frais de transport (art. L.3261-2).\n\nIl accède aux activités sociales et culturelles (art. L.2323-83) dans les mêmes conditions que les salariés."} />
            </label>
            <textarea
              value={form.avantagesPrive}
              onChange={e => set('avantagesPrive', e.target.value)}
              rows={3}
              placeholder="Tickets restaurant, remboursement transport…"
              className={`${inputClass} resize-none`}
            />
          </div>
          <div>
            <label className={labelClass}>
              Avantages accordés — droit public (Art. 5ter)
              <Tooltip text={"Article 5ter – Accès aux droits des agents (Organisme de droit public)\n\nLes trajets entre le domicile et le lieu de stage sont pris en charge dans les conditions fixées par le décret n°2010-676 du 21 juin 2010.\n\nLe stagiaire effectuant une mission dans le cadre d'un organisme de droit public bénéficie de la prise en charge de ses frais de déplacement temporaire selon la réglementation en vigueur.\n\nEst considéré comme résidence administrative le lieu du stage indiqué dans la convention."} />
            </label>
            <textarea
              value={form.avantagesPublic}
              onChange={e => set('avantagesPublic', e.target.value)}
              rows={3}
              placeholder="Indemnités, avantages en nature…"
              className={`${inputClass} resize-none`}
            />
          </div>
        </div>
      )}

      {/* Étape 4 : Représentant légal (si mineur) + Documents */}
      {step === 4 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
          <h3 className="font-medium text-gray-800">Étape 4 — Finalisation</h3>

          {dateNaissance && !form.startDate && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-500">
              La présence ou non d'un représentant légal sera déterminée automatiquement une fois la date de début de stage renseignée.
            </div>
          )}

          {dateNaissance && form.startDate && !form.isMineur && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-700">
              Vous serez majeur(e) au début du stage — aucun représentant légal requis.
            </div>
          )}

          {!dateNaissance && (
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="isMineur"
                checked={form.isMineur}
                onChange={e => set('isMineur', e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded"
              />
              <label htmlFor="isMineur" className="text-sm font-medium text-gray-700">
                Je suis mineur(e) — un représentant légal doit aussi signer
              </label>
            </div>
          )}

          {form.isMineur && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-3">
              {dateNaissance && form.startDate && (
                <p className="text-xs text-yellow-700">Vous serez mineur(e) au début du stage — un représentant légal doit aussi signer.</p>
              )}
              <h4 className="text-sm font-medium text-yellow-800">Représentant légal</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Nom</label>
                  <input type="text" value={form.repLegalNom} onChange={e => set('repLegalNom', e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Prénom</label>
                  <input type="text" value={form.repLegalPrenom} onChange={e => set('repLegalPrenom', e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Email</label>
                  <input type="email" value={form.repLegalEmail} onChange={e => set('repLegalEmail', e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Téléphone</label>
                  <input type="tel" value={form.repLegalTelephone} onChange={e => set('repLegalTelephone', e.target.value)} className={inputClass} />
                </div>
                <div className="col-span-2">
                  <label className={labelClass}>Lien (père, mère, tuteur légal…)</label>
                  <input type="text" value={form.repLegalLien} onChange={e => set('repLegalLien', e.target.value)} className={inputClass} />
                </div>
              </div>
            </div>
          )}

          {savedId && (
            <DocumentUpload conventionId={savedId} />
          )}
        </div>
      )}

      {/* Modal annexe PDF */}
      {showAnnexe && (() => {
        const cls = classes.find(c => c.id === form.classeId);
        if (!cls?.annexePdf) return null;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowAnnexe(false)}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl mx-4 flex flex-col" style={{ height: '80vh' }} onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <span className="font-medium text-gray-800 text-sm">{cls.annexeBoutonTexte || 'Document annexe'}</span>
                <div className="flex items-center gap-3">
                  <a
                    href={`/uploads/${cls.annexePdf}`}
                    download
                    className="text-xs text-blue-600 hover:text-blue-800 border border-blue-200 px-3 py-1.5 rounded-lg"
                  >
                    Télécharger
                  </a>
                  <button onClick={() => setShowAnnexe(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
                </div>
              </div>
              <div className="flex-1 overflow-hidden">
                <iframe
                  src={`/uploads/${cls.annexePdf}`}
                  title="Document annexe"
                  className="w-full h-full border-0"
                />
              </div>
            </div>
          </div>
        );
      })()}

      {/* Navigation */}
      <div className="flex items-center justify-between mt-6">
        <div className="flex gap-3">
          {step > 1 && (
            <button
              onClick={() => setStep(s => s - 1)}
              className="text-sm text-gray-600 hover:text-gray-800 border border-gray-300 px-4 py-2 rounded-lg transition-colors"
            >
              ← Précédent
            </button>
          )}
          {(() => {
            const cls = classes.find(c => c.id === form.classeId);
            if (!cls?.annexePdf) return null;
            return (
              <button
                type="button"
                onClick={() => setShowAnnexe(true)}
                className="inline-flex items-center gap-1.5 border border-blue-300 text-blue-700 hover:bg-blue-50 text-sm px-4 py-2 rounded-lg transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                {cls.annexeBoutonTexte || 'Consulter annexe'}
              </button>
            );
          })()}
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => handleSave(false)}
            disabled={saving}
            className="text-sm text-gray-700 border border-gray-300 hover:border-gray-400 px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer le brouillon'}
          </button>

          {step < 4 ? (
            <button
              onClick={() => setStep(s => s + 1)}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            >
              Suivant →
            </button>
          ) : (
            <button
              onClick={() => handleSave(true)}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            >
              {saving ? 'Envoi...' : 'Soumettre à l\'enseignant'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
